/**
 * Sign-in verification queue — READ-ONLY, flag-gated guided sign-in list.
 *
 * Sources GET /job-connections/verification-queue (JWT), the read-only queue
 * behind the backend flag SAIK_GUIDED_VERIFY_ENABLED. While the flag is OFF the
 * endpoint answers {enabled:false, steps:[]} and this card renders NOTHING, so
 * it is INERT in production. It also renders nothing if the request errors or
 * times out, or if the queue has no steps.
 *
 * When enabled:true it shows the remaining/total count and one ordered row per
 * step (sorted by `index`): domain (fallback site_id) + a friendly credential
 * hint + a "Pending" chip. The single placeholder control is a DISABLED
 * "Start" button clearly marked for the future flow — nothing here launches a
 * browser, logs in, or takes a credential.
 */
import { useEffect, useState } from 'react';
import { ListChecks } from 'lucide-react';
import Card from './Card';
import { jobbotApi, type VerificationQueueCounts, type VerificationQueueStep } from '../api/jobbot';

/** Keep a slow/dead backend from ever leaving a stray placeholder on screen. */
const REQUEST_TIMEOUT_MS = 8000;

/** Friendly label per credential hint (unknown → generic sign-in wording). */
const CREDENTIAL_HINT_LABEL: Record<string, string> = {
  login_password: 'Username & password',
  session_token: 'Session key',
  undetermined: 'Sign-in',
};

export default function VerificationQueueCard() {
  const [steps, setSteps] = useState<VerificationQueueStep[] | null>(null);
  const [counts, setCounts] = useState<VerificationQueueCounts | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
    (async () => {
      try {
        const res = await jobbotApi.extension.verificationQueue({ signal: controller.signal });
        const data = res.data;
        if (!alive) return;
        // Flag OFF → endpoint returns enabled:false → render nothing (inert).
        if (data?.enabled) {
          setSteps(data.steps || []);
          setCounts(data.counts || null);
        } else {
          setSteps(null);
          setCounts(null);
        }
      } catch {
        // Network error / timeout / disabled → render nothing.
        if (alive) {
          setSteps(null);
          setCounts(null);
        }
      } finally {
        window.clearTimeout(timer);
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
      controller.abort();
      window.clearTimeout(timer);
    };
  }, []);

  if (loading) return null; // never flash a placeholder before we know the flag
  if (!steps || steps.length === 0) return null; // flag OFF / error / empty → inert

  const ordered = [...steps].sort((a, b) => (a.index ?? 0) - (b.index ?? 0));
  const total = counts?.total ?? ordered.length;
  const remaining = counts?.remaining ?? ordered.length;

  return (
    <Card className="mb-6 p-4" data-agent-id="jobbot.sites.verification-queue">
      <div className="mb-3 flex items-center gap-2">
        <ListChecks size={18} className="text-brand-600" aria-hidden="true" />
        <h2 className="font-semibold text-slate-950 dark:text-slate-100">Sign-in verification queue</h2>
      </div>
      <p
        className="mb-3 text-xs text-slate-600 dark:text-slate-500"
        data-agent-id="jobbot.sites.verification-queue.remaining"
      >
        {remaining} of {total} sites still need sign-in
      </p>
      <ul className="space-y-2">
        {ordered.map((step) => {
          const name = step.domain || step.site_id;
          const hint = CREDENTIAL_HINT_LABEL[step.credential_type_hint] || 'Sign-in';
          return (
            <li
              key={step.site_id}
              data-agent-id={`jobbot.sites.verification-queue.row.${step.site_id}`}
              className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-slate-200 dark:border-slate-700 p-3"
            >
              <div className="min-w-0">
                <p className="truncate font-medium text-slate-950 dark:text-slate-100" title={name}>{name}</p>
                <span className="mt-1 inline-block rounded-md bg-slate-100 dark:bg-slate-700/50 px-2 py-0.5 text-xs text-slate-700 dark:text-slate-300">
                  {hint}
                </span>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <span className="rounded-md bg-amber-500/15 px-2 py-0.5 text-xs text-amber-700 dark:text-amber-400">
                  Pending
                </span>
              </div>
            </li>
          );
        })}
      </ul>
      <div className="mt-3 flex justify-end">
        <button
          type="button"
          disabled
          aria-disabled="true"
          title="Coming soon"
          className="cursor-not-allowed rounded-lg border border-slate-300 dark:border-slate-700 bg-slate-100 dark:bg-slate-800 px-3 py-1.5 text-sm text-slate-600 dark:text-slate-400"
        >
          Start
        </button>
      </div>
    </Card>
  );
}
